/**
 * Add Property Wizard
 * Property-type-first wizard for adding a new property. Basics + type first, then setup toggles, then conditional steps.
 * Supports mode 'create' (API) and 'collect' (signup flow; onCollected with summary shape).
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  AlertTriangle,
  Building,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  FileText,
  Info,
  Layers,
  Plus,
  Settings,
  Shield,
  Trash2,
  Upload,
  User,
  Users,
  X,
  Zap,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type { Property, Unit } from '@repo/shared-types';
import type { PropertyStatus, UnitStatus } from '@repo/shared-types/common/statuses';
import { useCreateProperty, useCreateUnit } from '../../hooks/queries/useProperties';
import { useCreateTenant } from '../../hooks/queries/useTenants';
import { useCreateLease } from '../../hooks/queries/useLeasing';
import { useToast } from '../common/ToastContext';
import { useNavigate } from '../../routes';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { DatePicker } from '../ui/DatePicker';
import { Select } from '../ui/Select';
import { FormErrorSummary } from '../ui/FormErrorSummary';
import { ROUTES } from '@repo/shared-types/routeRegistry';
import {
  REPAIR_APPROVAL_HELP,
  REPAIR_APPROVAL_OPTIONS_CENTS,
} from '../../utils/repairApprovalThresholdUi';
import { useAppStore } from '../../store';
import { pmProfileApi } from '../../services/api/pmProfile';
import { newClientEntityId } from '../../utils/ids';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

const PROPERTY_TYPES = ['Single Family', 'Duplex', 'Multifamily', 'Commercial'] as const;
const PROPERTY_STATUSES: PropertyStatus[] = ['Stable', 'At Risk', 'Lease-up', 'Renovation', 'Vacant'];
const UNIT_STATUSES: UnitStatus[] = ['Vacant', 'Occupied', 'Renovation'];

const AMENITIES = [
  'Parking',
  'In-unit Laundry',
  'Laundry Room',
  'AC',
  'Heating',
  'Balcony/Patio',
  'Pool',
  'Gym/Fitness',
  'Pet Friendly',
  'Storage',
  'Elevator',
  'EV Charging',
  'Doorman',
  'Rooftop',
  'Furnished',
  'Dishwasher',
  'Garbage Disposal',
  'Fireplace',
];

const PROGRAM_TYPES = ['LIHTC', 'HUD', 'State', 'Local'] as const;
const AMI_OPTIONS = ['30%', '40%', '50%', '60%', '70%', '80%', '100%', '120%'];

export interface MinimalTenantInput {
  name: string;
  email: string;
  phone: string;
  leaseStart: string;
  leaseEnd: string;
}

export interface UnitRow {
  tempId: string;
  unitNumber: string;
  type: Property['type'];
  status: UnitStatus;
  rent: number | '';
  sqft: number | '';
  bedrooms: number | '';
  bathrooms: number | '';
  addTenantNow: boolean;
  tenant?: MinimalTenantInput;
}

export interface PropertyOnboardingFormState {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  type: Property['type'];
  status: PropertyStatus;
  yearBuilt: string;

  sqft: string;
  bedrooms: string;
  bathrooms: string;
  description: string;
  amenities: string[];
  image: string;
  /** Is this home currently occupied? (Figma Property Details step) */
  isOccupied: boolean;

  unitCount: number;
  units: UnitRow[];

  marketRent: string;
  noi: string;
  occupancyRate: string;
  lateFeeEnabled: boolean;
  lateFeeDays: string;
  lateFeeType: 'fixed' | 'percentage' | 'combined';
  lateFeeAmount: string;
  lateFeePercentage: string;
  lateFeeMax: string;

  /** When SMO: 'self' = I manage myself, 'pm' = assign to PM. Only used when isSelfManagingOwner; PM (non-SMO) never sees this. */
  managementType: 'self' | 'pm';
  managementFeeType: 'percentage' | 'flat' | '';
  managementFeeAmount: string;
  bankAccountForRent: 'trust' | 'owner' | 'setup-later' | '';
  managerId: string;
  ownerIds: string[];
  portfolioId: string;
  investorIds: string[];

  affordableProgram: boolean;
  programType: 'LIHTC' | 'HUD' | 'State' | 'Local';
  compliancePeriodEnd: string;
  amiPercentage: string;
  setAsideUnits: string;
  complianceStatus: 'Compliant' | 'At Risk' | 'Non-Compliant';
  hcvEnabled: boolean;
  /** Lead-based paint disclosure (required when yearBuilt < 1978). */
  leadBasedPaintDisclosure: boolean;

  config: {
    trackUnits: boolean;
    trackFinancials: boolean;
    trackOperations: boolean;
    trackOwnership: boolean;
    trackCompliance: boolean;
  };
  operations: {
    utilityProvider: string;
    accessCode: string;
    autoSOP: boolean;
    /** Repair approval threshold in cents (e.g. 50000 = $500). */
    repairApprovalThresholdCents: number | '';
  };
}

const defaultUnitRow = (index: number): UnitRow => ({
  tempId: newClientEntityId(),
  unitNumber: String(index + 1),
  type: 'Multifamily',
  status: 'Vacant',
  rent: '',
  sqft: '',
  bedrooms: '',
  bathrooms: '',
  addTenantNow: false,
});

const initialFormState: PropertyOnboardingFormState = {
  name: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  type: 'Multifamily',
  status: 'Stable',
  yearBuilt: '',

  sqft: '',
  bedrooms: '',
  bathrooms: '',
  description: '',
  amenities: [],
  image: '',
  isOccupied: false,

  unitCount: 1,
  units: [defaultUnitRow(0)],

  marketRent: '',
  noi: '',
  occupancyRate: '',
  lateFeeEnabled: false,
  lateFeeDays: '5',
  lateFeeType: 'fixed',
  lateFeeAmount: '',
  lateFeePercentage: '',
  lateFeeMax: '',

  managementType: 'self',
  managementFeeType: '',
  managementFeeAmount: '',
  bankAccountForRent: '',
  managerId: '',
  ownerIds: [''],
  portfolioId: '',
  investorIds: [''],

  affordableProgram: false,
  programType: 'LIHTC',
  compliancePeriodEnd: '',
  amiPercentage: '60%',
  setAsideUnits: '',
  complianceStatus: 'Compliant',
  hcvEnabled: false,
  leadBasedPaintDisclosure: false,

  config: { trackUnits: true, trackFinancials: false, trackOperations: false, trackOwnership: false, trackCompliance: false },
  operations: { utilityProvider: '', accessCode: '', autoSOP: false, repairApprovalThresholdCents: '' },
};

// ---------------------------------------------------------------------------
// Step config
// ---------------------------------------------------------------------------

/** Summary shape for collect mode (signup "Your properties" step). */
export interface AddPropertyCollectedSummary {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  yearBuilt: string;
  type: Property['type'];
  status: PropertyStatus;
}

const MASTER_STEPS = [
  { id: 0, key: 'basics', title: 'Property Basics', subtitle: 'Tell us about this property', icon: Building, required: true },
  { id: 1, key: 'setup', title: 'What would you like to set up now?', subtitle: 'Choose what to configure (you can always add more later)', icon: Settings, required: true },
  { id: 2, key: 'details', title: 'Property Details', subtitle: 'Tell us about this home', icon: FileText, required: false },
  { id: 3, key: 'currentTenant', title: 'Current Tenant', subtitle: 'Enter the current tenant and lease details', icon: User, required: true, condition: (f: PropertyOnboardingFormState) => f.type === 'Single Family' && f.isOccupied },
  { id: 4, key: 'units', title: 'Units', subtitle: 'Current occupants and lease information', icon: Layers, required: false, condition: (f: PropertyOnboardingFormState) => f.config.trackUnits && f.type !== 'Single Family' },
  { id: 5, key: 'financials', title: 'Financials', subtitle: 'Set up rent and payment details', icon: DollarSign, required: false, condition: (f: PropertyOnboardingFormState) => f.config.trackFinancials },
  { id: 6, key: 'operations', title: 'Operations & Utilities', subtitle: 'Set up operational details', icon: Zap, required: false, condition: (f: PropertyOnboardingFormState) => f.config.trackOperations },
  { id: 7, key: 'ownership', title: 'Owner/Investor Information', subtitle: 'Define ownership structure for trust accounting and distributions', icon: Users, required: false, condition: (f: PropertyOnboardingFormState) => f.config.trackOwnership },
  { id: 8, key: 'compliance', title: 'Compliance & Regulations', subtitle: 'Regulatory requirements for this property', icon: Shield, required: false, condition: (f: PropertyOnboardingFormState) => f.config.trackCompliance },
  { id: 9, key: 'review', title: 'Review Property Details', subtitle: 'Review everything before adding this property', icon: CheckCircle2, required: true },
];

const SKIPPABLE_KEYS = new Set(['details', 'units', 'financials', 'operations', 'ownership', 'compliance']);

function SetupChecklist({
  config,
  update,
}: {
  config: PropertyOnboardingFormState['config'];
  update: (config: PropertyOnboardingFormState['config']) => void;
}) {
  const toggle = (key: keyof PropertyOnboardingFormState['config']) => {
    update({ ...config, [key]: !config[key] });
  };
  const options = [
    { id: 'trackFinancials' as const, title: 'Financials', description: 'Rent amounts, bank accounts, management fees', icon: DollarSign },
    { id: 'trackOperations' as const, title: 'Operations', description: 'Utilities, maintenance, insurance details', icon: Zap },
    { id: 'trackUnits' as const, title: 'Tenants & Leasing', description: 'Current occupants and lease information', icon: Layers },
    { id: 'trackCompliance' as const, title: 'Compliance', description: 'Section 8, lead paint, rent control', icon: Shield },
    { id: 'trackOwnership' as const, title: 'Owner/Investor', description: 'Ownership structure for trust accounting and distributions', icon: Users },
  ];
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-4">
        {options.map((opt) => {
          const isOn = config[opt.id];
          const Icon = opt.icon;
          return (
            <div
              key={opt.id}
              className="flex items-center justify-between gap-4 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 shadow-sm"
            >
              <div className="flex items-center gap-4 min-w-0">
                <div className="p-2.5 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 shrink-0">
                  <Icon size={22} />
                </div>
                <div className="min-w-0">
                  <h4 className="font-bold text-slate-900 dark:text-white">{opt.title}</h4>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{opt.description}</p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={isOn}
                onClick={() => toggle(opt.id)}
                className={`relative shrink-0 w-11 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 ${isOn ? 'bg-brand-600' : 'bg-slate-300 dark:bg-slate-600'}`}
              >
                <span
                  className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${isOn ? 'translate-x-5' : 'translate-x-0'}`}
                />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared form primitives
// ---------------------------------------------------------------------------

const inputClass =
  'w-full border rounded-lg p-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white border-slate-300 dark:border-slate-600 focus:ring-2 focus:ring-brand-500 outline-none';
const labelClass = 'block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1';

function Field({
  label,
  optional,
  help,
  children,
}: {
  label: string;
  optional?: boolean;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-4">
      <label className={labelClass}>
        {label}
        {optional && <span className="text-amber-600 dark:text-amber-400 ml-1 text-xs font-normal">(optional)</span>}
      </label>
      {help && <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{help}</p>}
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step components
// ---------------------------------------------------------------------------

function StepBasics({
  form,
  update,
  errors,
  isSelfManagingOwner,
  publishedPmProfiles = [],
}: {
  form: PropertyOnboardingFormState;
  update: (key: keyof PropertyOnboardingFormState, value: unknown) => void;
  errors: FormErrors;
  isSelfManagingOwner?: boolean;
  publishedPmProfiles?: { id: string; userId: string; companyName: string; user?: { fullName: string } }[];
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        Tell us about this property — you can fill in more later.
      </p>
      <Field label="Year Built" optional>
        <input
          type="text"
          value={form.yearBuilt}
          onChange={(e) => update('yearBuilt', e.target.value)}
          placeholder="e.g., 1985"
          className={inputClass}
        />
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Required for lead-based paint disclosure (pre-1978).</p>
      </Field>
      <Field label="Property Name">
        <input
          type="text"
          value={form.name}
          onChange={(e) => update('name', e.target.value)}
          placeholder="e.g. Maple Grove Apartments"
          className={inputClass}
          data-field="name"
        />
        {errors.name?.message && <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>}
      </Field>
      <Field label="Street Address">
        <input
          type="text"
          value={form.address}
          onChange={(e) => update('address', e.target.value)}
          placeholder="123 Main Street"
          className={inputClass}
          data-field="address"
        />
        {errors.address?.message && <p className="text-xs text-red-500 mt-1">{errors.address.message}</p>}
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="City" optional>
          <input
            type="text"
            value={form.city}
            onChange={(e) => update('city', e.target.value)}
            placeholder="Portland"
            className={inputClass}
          />
        </Field>
        <Field label="State" optional>
          <input
            type="text"
            value={form.state}
            onChange={(e) => update('state', e.target.value)}
            placeholder="OR"
            className={inputClass}
          />
        </Field>
        <Field label="ZIP" optional>
          <input
            type="text"
            value={form.zip}
            onChange={(e) => update('zip', e.target.value)}
            placeholder="97201"
            className={inputClass}
          />
        </Field>
      </div>
      <Field label="Property Type">
        <div className="flex flex-wrap gap-2">
          {PROPERTY_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                if (form.type === t) return;
                if (hasSignificantData(form) && !window.confirm('Changing property type may reset some options. Do you want to continue?')) return;
                update('type', t);
                if (t === 'Single Family') update('config', { ...form.config, trackUnits: false });
              }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${form.type === t
                  ? 'bg-brand-600 text-white border-brand-600'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
                }`}
            >
              {t}
            </button>
          ))}
        </div>
        {form.type === 'Single Family' && (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">Single family: units step is skipped; the property is treated as one unit.</p>
        )}
      </Field>
      <Field label="Current Status">
        <div className="flex flex-wrap gap-2">
          {PROPERTY_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => update('status', s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${form.status === s
                  ? 'bg-brand-600 text-white border-brand-600'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
                }`}
            >
              {s}
            </button>
          ))}
        </div>
      </Field>

      {isSelfManagingOwner && (
        <>
          <Field label="How is this property managed?">
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
              This determines who has operational control and affects trust accounting.
            </p>
            <div className="space-y-3">
              <label className="flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition border-slate-200 dark:border-slate-700 hover:border-brand-300 dark:hover:border-brand-700 has-[:checked]:border-brand-600 has-[:checked]:bg-brand-50 dark:has-[:checked]:bg-brand-900/20">
                <input
                  type="radio"
                  name="managementType"
                  checked={form.managementType === 'self'}
                  onChange={() => { update('managementType', 'self'); update('managerId', ''); }}
                  className="mt-1"
                />
                <div>
                  <span className="font-medium text-slate-900 dark:text-white">I manage this property myself</span>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Full operational control, no PM fees.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition border-slate-200 dark:border-slate-700 hover:border-brand-300 dark:hover:border-brand-700 has-[:checked]:border-brand-600 has-[:checked]:bg-brand-50 dark:has-[:checked]:bg-brand-900/20">
                <input
                  type="radio"
                  name="managementType"
                  checked={form.managementType === 'pm'}
                  onChange={() => update('managementType', 'pm')}
                  className="mt-1"
                />
                <div>
                  <span className="font-medium text-slate-900 dark:text-white">Assign to a Property Manager</span>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">PM handles day-to-day operations.</p>
                </div>
              </label>
            </div>
          </Field>
          {form.managementType === 'pm' && (
            <div className="rounded-lg border-2 border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20 p-4">
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle size={20} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <p className="font-semibold text-amber-900 dark:text-amber-200">Important: Select a property manager</p>
              </div>
              <Field label="Select Property Manager *">
                <Select
                  options={[
                    { value: '', label: '— Choose a property manager —' },
                    ...publishedPmProfiles.map((pm) => ({
                      value: pm.userId,
                      label: `${pm.companyName} — ${pm.user?.fullName ?? 'PM'}`,
                    })),
                  ]}
                  value={form.managerId}
                  onChange={(e) => update('managerId', e.target.value)}
                  className={inputClass}
                  data-field="managerId"
                />
              </Field>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StepDetails({
  form,
  update,
}: {
  form: PropertyOnboardingFormState;
  update: (key: keyof PropertyOnboardingFormState, value: unknown) => void;
}) {
  const toggleAmenity = (a: string) => {
    const next = form.amenities.includes(a) ? form.amenities.filter((x) => x !== a) : [...form.amenities, a];
    update('amenities', next);
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        Physical characteristics and what makes this property special.
      </p>
      <Field label="Is this home currently occupied? *">
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="isOccupied"
              checked={form.isOccupied === true}
              onChange={() => update('isOccupied', true)}
              className="w-4 h-4 text-brand-600"
            />
            <span className="text-sm font-medium text-slate-900 dark:text-white">Yes, there is a tenant</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="isOccupied"
              checked={form.isOccupied === false}
              onChange={() => update('isOccupied', false)}
              className="w-4 h-4 text-brand-600"
            />
            <span className="text-sm font-medium text-slate-900 dark:text-white">No, it&apos;s vacant</span>
          </label>
        </div>
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Square Footage" optional>
          <input
            type="number"
            value={form.sqft}
            onChange={(e) => update('sqft', e.target.value)}
            placeholder="1,200"
            className={inputClass}
          />
        </Field>
        {form.type !== 'Multifamily' && form.type !== 'Commercial' && (
          <>
            <Field label="Bedrooms" optional>
              <input
                type="number"
                value={form.bedrooms}
                onChange={(e) => update('bedrooms', e.target.value)}
                placeholder="3"
                className={inputClass}
              />
            </Field>
            <Field label="Bathrooms" optional>
              <input
                type="number"
                value={form.bathrooms}
                onChange={(e) => update('bathrooms', e.target.value)}
                placeholder="2"
                className={inputClass}
              />
            </Field>
          </>
        )}
      </div>
      <Field label="Description" optional>
        <textarea
          value={form.description}
          onChange={(e) => update('description', e.target.value)}
          placeholder="Describe what makes this property stand out..."
          rows={3}
          className={inputClass + ' resize-y'}
        />
      </Field>
      <Field label="Amenities" optional>
        <div className="flex flex-wrap gap-2">
          {AMENITIES.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => toggleAmenity(a)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${form.amenities.includes(a)
                  ? 'bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 border-brand-300 dark:border-brand-700'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-600'
                }`}
            >
              {a}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Cover Image URL" optional>
        <input
          type="url"
          value={form.image}
          onChange={(e) => update('image', e.target.value)}
          placeholder="https://..."
          className={inputClass}
        />
        <div className="mt-2 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-6 text-center text-slate-500 dark:text-slate-400 text-sm">
          <Upload size={24} className="mx-auto mb-2 opacity-50" />
          Or add photos later from the property page
        </div>
      </Field>
    </div>
  );
}

function StepCurrentTenant({
  form,
  update,
}: {
  form: PropertyOnboardingFormState;
  update: (key: keyof PropertyOnboardingFormState, value: unknown) => void;
}) {
  const unit = form.units[0];
  const tenant = unit?.tenant ?? { name: '', email: '', phone: '', leaseStart: '', leaseEnd: '' };
  const setSingleFamilyTenant = (next: MinimalTenantInput) => {
    const nextUnits = [...form.units];
    if (nextUnits.length === 0) nextUnits.push(defaultUnitRow(0));
    nextUnits[0] = {
      ...nextUnits[0],
      status: 'Occupied',
      addTenantNow: true,
      tenant: next,
      rent: typeof nextUnits[0].rent === 'number' ? nextUnits[0].rent : '',
    };
    update('units', nextUnits);
    update('unitCount', nextUnits.length);
  };
  const setRent = (value: number | '') => {
    const nextUnits = [...form.units];
    if (nextUnits.length === 0) nextUnits.push(defaultUnitRow(0));
    nextUnits[0] = { ...nextUnits[0], status: 'Occupied', addTenantNow: true, rent: value, tenant: nextUnits[0].tenant };
    update('units', nextUnits);
  };
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 p-4 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
        <Info size={20} className="text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800 dark:text-blue-200">
          This home is occupied. Enter the current tenant and lease details below — you can add or edit this information later from the property page.
        </p>
      </div>
      <Field label="Tenant name *">
        <input
          type="text"
          value={tenant.name}
          onChange={(e) => setSingleFamilyTenant({ ...tenant, name: e.target.value })}
          placeholder="e.g. Jane Smith"
          className={inputClass}
        />
      </Field>
      <Field label="Email *">
        <input
          type="email"
          value={tenant.email}
          onChange={(e) => setSingleFamilyTenant({ ...tenant, email: e.target.value })}
          placeholder="tenant@example.com"
          className={inputClass}
        />
      </Field>
      <Field label="Phone" optional>
        <input
          type="tel"
          value={tenant.phone}
          onChange={(e) => setSingleFamilyTenant({ ...tenant, phone: e.target.value })}
          placeholder="(555) 123-4567"
          className={inputClass}
        />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <DatePicker
          label="Lease start *"
          value={tenant.leaseStart}
          onChange={(e) => setSingleFamilyTenant({ ...tenant, leaseStart: e.target.value })}
          className={inputClass}
        />
        <DatePicker
          label="Lease end *"
          value={tenant.leaseEnd}
          onChange={(e) => setSingleFamilyTenant({ ...tenant, leaseEnd: e.target.value })}
          className={inputClass}
        />
      </div>
      <Field label="Monthly rent *">
        <input
          type="number"
          min={0}
          value={unit?.rent === '' ? '' : unit?.rent}
          onChange={(e) => setRent(e.target.value === '' ? '' : Number(e.target.value))}
          placeholder="1,500"
          className={inputClass}
        />
      </Field>
    </div>
  );
}

function StepUnits({
  form,
  update,
  errors,
}: {
  form: PropertyOnboardingFormState;
  update: (key: keyof PropertyOnboardingFormState, value: unknown) => void;
  errors: FormErrors;
}) {
  const setUnits = useCallback(
    (next: UnitRow[]) => {
      update('units', next);
      update('unitCount', next.length);
    },
    [update]
  );

  const addUnit = () => setUnits([...form.units, defaultUnitRow(form.units.length)]);
  const removeUnit = (tempId: string) => setUnits(form.units.filter((u) => u.tempId !== tempId));

  const updateUnit = (tempId: string, field: keyof UnitRow, value: unknown) => {
    setUnits(
      form.units.map((u) => (u.tempId === tempId ? { ...u, [field]: value } : u))
    );
  };

  const setUnitTenant = (tempId: string, tenant: MinimalTenantInput | undefined) => {
    setUnits(
      form.units.map((u) => (u.tempId === tempId ? { ...u, tenant, addTenantNow: !!tenant } : u))
    );
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        Define individual units. For occupied units, you can add the tenant now or later.
      </p>
      <div className="flex items-center gap-4">
        <div className="w-32">
          <Field label="Total units">
            <input
              type="number"
              min={1}
              value={form.unitCount}
              onChange={(e) => {
                const n = Math.max(1, Number.parseInt(e.target.value, 10) || 1);
                update('unitCount', n);
                if (n > form.units.length) {
                  const extras = Array.from({ length: n - form.units.length }, (_, i) =>
                    defaultUnitRow(form.units.length + i)
                  );
                  setUnits([...form.units, ...extras]);
                } else {
                  setUnits(form.units.slice(0, n));
                }
              }}
              className={inputClass}
            />
          </Field>
        </div>
        <span className="text-sm text-slate-500 dark:text-slate-400 pt-6">
          {form.units.length} unit{form.units.length === 1 ? '' : 's'} below
        </span>
      </div>

      <div className="space-y-3">
        {form.units.map((unit, i) => (
          <div
            key={unit.tempId}
            className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 overflow-hidden"
          >
            <div className="px-4 py-2 bg-brand-50 dark:bg-brand-900/20 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <span className="text-sm font-semibold text-brand-700 dark:text-brand-300">
                Unit {unit.unitNumber || i + 1}
              </span>
              {form.units.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeUnit(unit.tempId)}
                  className="text-red-600 dark:text-red-400 text-xs font-medium px-2 py-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                >
                  Remove
                </button>
              )}
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Unit number">
                  <input
                    type="text"
                    value={unit.unitNumber}
                    onChange={(e) => updateUnit(unit.tempId, 'unitNumber', e.target.value)}
                    placeholder="1A"
                    className={inputClass}
                  />
                </Field>
                <Field label="Status">
                  <Select
                    options={UNIT_STATUSES.map((s) => ({ value: s, label: s }))}
                    value={unit.status}
                    onChange={(e) => updateUnit(unit.tempId, 'status', e.target.value as UnitStatus)}
                    className={inputClass}
                  />
                </Field>
                <Field label="Monthly rent" optional>
                  <input
                    type="number"
                    min={0}
                    value={unit.rent === '' ? '' : unit.rent}
                    onChange={(e) =>
                      updateUnit(unit.tempId, 'rent', e.target.value === '' ? '' : Number(e.target.value))
                    }
                    placeholder="1,500"
                    className={inputClass}
                  />
                </Field>
                <Field label="Sq ft" optional>
                  <input
                    type="number"
                    min={0}
                    value={unit.sqft === '' ? '' : unit.sqft}
                    onChange={(e) =>
                      updateUnit(unit.tempId, 'sqft', e.target.value === '' ? '' : Number(e.target.value))
                    }
                    placeholder="850"
                    className={inputClass}
                  />
                </Field>
                <Field label="Bedrooms" optional>
                  <input
                    type="number"
                    min={0}
                    value={unit.bedrooms === '' ? '' : unit.bedrooms}
                    onChange={(e) =>
                      updateUnit(unit.tempId, 'bedrooms', e.target.value === '' ? '' : Number(e.target.value))
                    }
                    placeholder="2"
                    className={inputClass}
                  />
                </Field>
                <Field label="Bathrooms" optional>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={unit.bathrooms === '' ? '' : unit.bathrooms}
                    onChange={(e) =>
                      updateUnit(unit.tempId, 'bathrooms', e.target.value === '' ? '' : Number(e.target.value))
                    }
                    placeholder="1"
                    className={inputClass}
                  />
                </Field>
              </div>

              {unit.status === 'Occupied' && (
                <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      id={`add-tenant-${unit.tempId}`}
                      checked={unit.addTenantNow}
                      onChange={(e) => {
                        if (e.target.checked) setUnitTenant(unit.tempId, { name: '', email: '', phone: '', leaseStart: '', leaseEnd: '' });
                        else setUnitTenant(unit.tempId, undefined);
                      }}
                      className="rounded border-slate-300"
                    />
                    <label htmlFor={`add-tenant-${unit.tempId}`} className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Add tenant now
                    </label>
                  </div>
                  {unit.addTenantNow && unit.tenant && (
                    <div className="grid grid-cols-2 gap-3 bg-white dark:bg-slate-800/50 rounded-lg p-3">
                      <Field label="Tenant name">
                        <input
                          type="text"
                          value={unit.tenant.name}
                          onChange={(e) =>
                            setUnitTenant(unit.tempId, { ...unit.tenant!, name: e.target.value })
                          }
                          placeholder="Full name"
                          className={inputClass}
                        />
                      </Field>
                      <Field label="Email">
                        <input
                          type="email"
                          value={unit.tenant.email}
                          onChange={(e) =>
                            setUnitTenant(unit.tempId, { ...unit.tenant!, email: e.target.value })
                          }
                          placeholder="email@example.com"
                          className={inputClass}
                        />
                      </Field>
                      <Field label="Phone">
                        <input
                          type="tel"
                          value={unit.tenant.phone}
                          onChange={(e) =>
                            setUnitTenant(unit.tempId, { ...unit.tenant!, phone: e.target.value })
                          }
                          placeholder="(555) 000-0000"
                          className={inputClass}
                        />
                      </Field>
                      <DatePicker
                        label="Lease start"
                        value={unit.tenant.leaseStart}
                        onChange={(e) =>
                          setUnitTenant(unit.tempId, { ...unit.tenant!, leaseStart: e.target.value })
                        }
                        className={inputClass}
                      />
                      <DatePicker
                        label="Lease end"
                        value={unit.tenant.leaseEnd}
                        onChange={(e) =>
                          setUnitTenant(unit.tempId, { ...unit.tenant!, leaseEnd: e.target.value })
                        }
                        className={inputClass}
                      />
                    </div>
                  )}
                  {unit.status === 'Occupied' && !unit.addTenantNow && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">You can add this tenant later from the property page.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addUnit}
        className="w-full py-2.5 border-2 border-dashed border-brand-400 dark:border-brand-500 rounded-lg text-brand-600 dark:text-brand-400 font-medium text-sm hover:bg-brand-50 dark:hover:bg-brand-900/20 transition flex items-center justify-center gap-2"
      >
        <Plus size={18} /> Add another unit
      </button>
      {errors.units?.message && <p className="text-xs text-red-500">{errors.units.message}</p>}
    </div>
  );
}

function StepFinancials({ form, update }: { form: PropertyOnboardingFormState; update: (k: keyof PropertyOnboardingFormState, v: unknown) => void }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        Financial profile. All fields are optional at this stage.
      </p>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Market rent ($/mo)" optional help="Monthly market rate for this property">
          <input
            type="number"
            min={0}
            value={form.marketRent}
            onChange={(e) => update('marketRent', e.target.value)}
            placeholder="1,500"
            className={inputClass}
          />
        </Field>
      </div>
      {form.managementType === 'pm' && (
        <div className="grid grid-cols-2 gap-4 border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-slate-50/50 dark:bg-slate-800/30">
          <Field label="Management fee structure">
            <Select
              options={[
                { value: '', label: 'Select...' },
                { value: 'percentage', label: 'Percentage of rent' },
                { value: 'flat', label: 'Flat monthly fee' },
              ]}
              value={form.managementFeeType}
              onChange={(e) => update('managementFeeType', e.target.value as PropertyOnboardingFormState['managementFeeType'])}
              className={inputClass}
            />
          </Field>
          {(form.managementFeeType === 'percentage' || form.managementFeeType === 'flat') && (
            <Field label="Fee amount" optional help="Enter percentage (e.g., 8) or dollar amount">
              <input
                type="number"
                min={0}
                value={form.managementFeeAmount}
                onChange={(e) => update('managementFeeAmount', e.target.value)}
                placeholder="8"
                className={inputClass}
              />
            </Field>
          )}
        </div>
      )}
      <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-slate-50/50 dark:bg-slate-800/30">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Late fee policy</span>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.lateFeeEnabled}
              onChange={(e) => update('lateFeeEnabled', e.target.checked)}
              className="rounded border-slate-300"
            />
            <span className="text-sm text-slate-600 dark:text-slate-400">Enable late fees</span>
          </label>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Charge fees for late rent payments</p>
        {form.lateFeeEnabled && (
          <div className="grid grid-cols-2 gap-3 mt-2">
            <Field label="Late fee type">
              <select
                value={form.lateFeeType}
                onChange={(e) => update('lateFeeType', e.target.value as PropertyOnboardingFormState['lateFeeType'])}
                className={inputClass}
              >
                <option value="fixed">Flat Fee — Fixed dollar amount (e.g., $50)</option>
                <option value="percentage">Percentage — Percentage of rent (e.g., 5%)</option>
                <option value="combined">Combined — Flat fee + percentage</option>
              </select>
            </Field>
            <Field label="Grace period (days)" help="Days after due date before late fee applies">
              <input
                type="number"
                min={0}
                value={form.lateFeeDays}
                onChange={(e) => update('lateFeeDays', e.target.value)}
                placeholder="5"
                className={inputClass}
              />
            </Field>
            {(form.lateFeeType === 'fixed' || form.lateFeeType === 'combined') && (
              <Field label="Flat fee amount ($)" help="Dollar amount charged as late fee">
                <input
                  type="number"
                  min={0}
                  value={form.lateFeeAmount}
                  onChange={(e) => update('lateFeeAmount', e.target.value)}
                  placeholder="50"
                  className={inputClass}
                />
              </Field>
            )}
            {(form.lateFeeType === 'percentage' || form.lateFeeType === 'combined') && (
              <>
                <Field label="Percentage (%)" help="Percentage of monthly rent">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={form.lateFeePercentage}
                    onChange={(e) => update('lateFeePercentage', e.target.value)}
                    placeholder="5"
                    className={inputClass}
                  />
                </Field>
                <Field label="Max late fee ($)" optional help="Cap on total late fees charged">
                  <input
                    type="number"
                    min={0}
                    value={form.lateFeeMax}
                    onChange={(e) => update('lateFeeMax', e.target.value)}
                    placeholder="200"
                    className={inputClass}
                  />
                </Field>
              </>
            )}
          </div>
        )}
      </div>
      <Field label="Rent deposit destination" optional help="You can receive rent directly even if you use a property manager">
        <select
          value={form.bankAccountForRent}
          onChange={(e) => update('bankAccountForRent', e.target.value as PropertyOnboardingFormState['bankAccountForRent'])}
          className={inputClass}
        >
          <option value="">Select...</option>
          <option value="trust">Trust Account (managed by PM)</option>
          <option value="owner">My Bank Account (direct deposit)</option>
          <option value="setup-later">I&apos;ll set this up later</option>
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-200 dark:border-slate-700">
        <Field label="NOI" optional>
          <input
            type="number"
            value={form.noi}
            onChange={(e) => update('noi', e.target.value)}
            placeholder="48000"
            className={inputClass}
          />
        </Field>
        <Field label="Occupancy rate (%)" optional>
          <input
            type="number"
            min={0}
            max={100}
            value={form.occupancyRate}
            onChange={(e) => update('occupancyRate', e.target.value)}
            placeholder="95"
            className={inputClass}
          />
        </Field>
      </div>
    </div>
  );
}

function StepOwnership({
  form,
  update,
  isSelfManagingOwner,
}: {
  form: PropertyOnboardingFormState;
  update: (key: keyof PropertyOnboardingFormState, value: unknown) => void;
  isSelfManagingOwner?: boolean;
}) {
  const addOwner = () => update('ownerIds', [...form.ownerIds, '']);
  const setOwner = (i: number, v: string) => {
    const next = [...form.ownerIds];
    next[i] = v;
    update('ownerIds', next);
  };
  const removeOwner = (i: number) => update('ownerIds', form.ownerIds.filter((_, j) => j !== i));

  const addInvestor = () => update('investorIds', [...form.investorIds, '']);
  const setInvestor = (i: number, v: string) => {
    const next = [...form.investorIds];
    next[i] = v;
    update('investorIds', next);
  };
  const removeInvestor = (i: number) => update('investorIds', form.investorIds.filter((_, j) => j !== i));

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        Define ownership structure for trust accounting and distributions. You can link owners later from the property page.
      </p>
      {isSelfManagingOwner && (
        <Field label="Property manager" optional>
          <input
            type="text"
            value={form.managerId}
            onChange={(e) => update('managerId', e.target.value)}
            placeholder="Manager name or ID (if not set in Basics)"
            className={inputClass}
          />
        </Field>
      )}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className={labelClass}>Owners</span>
          <button type="button" onClick={addOwner} className="text-brand-600 dark:text-brand-400 text-sm font-medium flex items-center gap-1">
            <Plus size={14} /> Add owner
          </button>
        </div>
        {form.ownerIds.map((id, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input
              type="text"
              value={id}
              onChange={(e) => setOwner(i, e.target.value)}
              placeholder={`Owner ${i + 1} name or ID`}
              className={inputClass + ' flex-1'}
            />
            {form.ownerIds.length > 1 && (
              <button type="button" onClick={() => removeOwner(i)} className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg">
                <Trash2 size={16} />
              </button>
            )}
          </div>
        ))}
      </div>
      <Field label="Portfolio" optional>
        <input
          type="text"
          value={form.portfolioId}
          onChange={(e) => update('portfolioId', e.target.value)}
          placeholder="Portfolio name or ID"
          className={inputClass}
        />
      </Field>
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className={labelClass}>Investors (optional)</span>
          <button type="button" onClick={addInvestor} className="text-brand-600 dark:text-brand-400 text-sm font-medium flex items-center gap-1">
            <Plus size={14} /> Add
          </button>
        </div>
        {form.investorIds.map((id, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input
              type="text"
              value={id}
              onChange={(e) => setInvestor(i, e.target.value)}
              placeholder={`Investor ${i + 1}`}
              className={inputClass + ' flex-1'}
            />
            {form.investorIds.length > 1 && (
              <button type="button" onClick={() => removeInvestor(i)} className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg">
                <Trash2 size={16} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StepCompliance({ form, update }: { form: PropertyOnboardingFormState; update: (k: keyof PropertyOnboardingFormState, v: unknown) => void }) {
  const yearBuiltNum = form.yearBuilt ? Number.parseInt(form.yearBuilt, 10) : Number.NaN;
  const requiresLeadPaintDisclosure = !isNaN(yearBuiltNum) && yearBuiltNum < 1978;

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        Regulatory requirements for this property. Skip if not applicable.
      </p>
      {requiresLeadPaintDisclosure && (
        <label className="flex items-start gap-3 p-4 rounded-xl border-2 border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/20 cursor-pointer mb-4">
          <input
            type="checkbox"
            checked={form.leadBasedPaintDisclosure}
            onChange={(e) => update('leadBasedPaintDisclosure', e.target.checked)}
            className="mt-1 rounded border-slate-300"
          />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            I acknowledge this property was built before 1978 and requires lead-based paint disclosure.
          </span>
        </label>
      )}
      <label className="flex items-center gap-2 cursor-pointer mb-3">
        <input
          type="checkbox"
          checked={form.affordableProgram}
          onChange={(e) => update('affordableProgram', e.target.checked)}
          className="rounded border-slate-300"
        />
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Affordable housing participation</span>
      </label>
      {form.affordableProgram && (
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 space-y-3">
          <Field label="Program type">
            <select
              value={form.programType}
              onChange={(e) => update('programType', e.target.value as PropertyOnboardingFormState['programType'])}
              className={inputClass}
            >
              {PROGRAM_TYPES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <DatePicker
              label="Compliance period end (optional)"
              value={form.compliancePeriodEnd}
              onChange={(e) => update('compliancePeriodEnd', e.target.value)}
              className={inputClass}
            />
            <Field label="Set-aside units" optional>
              <input
                type="number"
                min={0}
                value={form.setAsideUnits}
                onChange={(e) => update('setAsideUnits', e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="AMI %" optional>
              <select
                value={form.amiPercentage}
                onChange={(e) => update('amiPercentage', e.target.value)}
                className={inputClass}
              >
                {AMI_OPTIONS.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </Field>
            <Field label="Compliance status" optional>
              <select
                value={form.complianceStatus}
                onChange={(e) => update('complianceStatus', e.target.value as PropertyOnboardingFormState['complianceStatus'])}
                className={inputClass}
              >
                <option value="Compliant">Compliant</option>
                <option value="At Risk">At Risk</option>
                <option value="Non-Compliant">Non-Compliant</option>
              </select>
            </Field>
          </div>
        </div>
      )}
      <label className="flex items-center gap-2 cursor-pointer mt-4">
        <input
          type="checkbox"
          checked={form.hcvEnabled}
          onChange={(e) => update('hcvEnabled', e.target.checked)}
          className="rounded border-slate-300"
        />
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Section 8 / Housing Choice Voucher (HCV) acceptance</span>
      </label>
    </div>
  );
}

function StepOperations({ form, update }: { form: PropertyOnboardingFormState; update: (k: 'operations', v: unknown) => void }) {
  const handleChange = (k: keyof PropertyOnboardingFormState['operations'], v: unknown) => {
    update('operations', { ...form.operations, [k]: v });
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        Set up operational details.
      </p>
      <div className="grid grid-cols-1 gap-4">
        <Field label="Utility provider" optional help="Electric or primary utility (e.g., Pacific Power)">
          <input type="text" value={form.operations.utilityProvider} onChange={e => handleChange('utilityProvider', e.target.value)} className={inputClass} placeholder="e.g. Pacific Power" />
        </Field>
        <Field label="Repair approval threshold" optional help={REPAIR_APPROVAL_HELP}>
          <select
            value={form.operations.repairApprovalThresholdCents === '' ? '' : form.operations.repairApprovalThresholdCents}
            onChange={e => handleChange('repairApprovalThresholdCents', e.target.value === '' ? '' : Number(e.target.value))}
            className={inputClass}
          >
            <option value="">No approval gate</option>
            {REPAIR_APPROVAL_OPTIONS_CENTS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Access code" optional help="Master lockbox or property access code">
          <input type="text" value={form.operations.accessCode} onChange={e => handleChange('accessCode', e.target.value)} className={inputClass} placeholder="e.g. 1234" />
        </Field>
      </div>
      <label className="flex items-center gap-2 cursor-pointer mt-4 border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-slate-50/50 dark:bg-slate-800/30">
        <input type="checkbox" checked={form.operations.autoSOP} onChange={e => handleChange('autoSOP', e.target.checked)} className="rounded border-slate-300" />
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Maintenance: Enroll in Automated Turn SOPs standard template</span>
      </label>
    </div>
  );
}

function StepReview({
  form,
  skipped,
  visibleSteps,
}: {
  form: PropertyOnboardingFormState;
  skipped: Set<string>;
  visibleSteps: typeof MASTER_STEPS;
}) {
  const skippedStepNames = visibleSteps.filter((s) => skipped.has(s.key) && s.key !== 'review').map((s) => s.title);
  const addressLine = [form.address, form.city, form.state, form.zip].filter(Boolean).join(', ');
  return (
    <div className="space-y-4">
      <div className="rounded-lg border-2 border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-4 flex items-start gap-3">
        <CheckCircle2 size={22} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
        <div>
          <p className="font-bold text-emerald-900 dark:text-emerald-100">Almost Done!</p>
          <p className="text-sm text-emerald-800 dark:text-emerald-200 mt-0.5">Please review the information below before adding this property.</p>
        </div>
      </div>
      <p className="text-sm text-slate-600 dark:text-slate-400">
        Review your property. You can add or edit details anytime from the property page.
      </p>
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 p-4">
        <h3 className="font-semibold text-slate-900 dark:text-white text-lg">{form.name || 'Unnamed Property'}</h3>
        {addressLine && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{addressLine}</p>}
        <div className="flex flex-wrap gap-2 mt-2">
          <span className="px-2 py-0.5 rounded-full bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 text-xs font-medium">{form.type}</span>
          <span className="px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-medium">{form.status}</span>
          <span className="px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-medium">
            {form.units.length} unit{form.units.length === 1 ? '' : 's'}
          </span>
          {form.yearBuilt && (
            <span className="px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-medium">Built {form.yearBuilt}</span>
          )}
        </div>
      </div>
      {skippedStepNames.length > 0 && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Skipped: {skippedStepNames.join(', ')}. You can add these from the property page after creation.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

type FormErrors = Record<string, { message: string }>;

function hasSignificantData(form: PropertyOnboardingFormState): boolean {
  if (form.name?.trim() && form.name.trim().length >= 2) return true;
  if (form.address?.trim() && form.address.trim().length >= 5) return true;
  if (form.city?.trim() || form.state?.trim() || form.zip?.trim() || form.yearBuilt?.trim()) return true;
  if (form.description?.trim()) return true;
  if (form.units.some((u) => u.unitNumber || u.rent || u.sqft || (u.tenant && (u.tenant.name || u.tenant.email)))) return true;
  return false;
}

function validateBasics(form: PropertyOnboardingFormState): FormErrors {
  const err: FormErrors = {};
  if (!form.name?.trim()) err.name = { message: 'Property name is required' };
  else if (form.name.trim().length < 2) err.name = { message: 'Name must be at least 2 characters' };
  if (!form.address?.trim()) err.address = { message: 'Address is required' };
  else if (form.address.trim().length < 5) err.address = { message: 'Please enter a complete address' };
  return err;
}

function validateUnits(form: PropertyOnboardingFormState): FormErrors {
  const err: FormErrors = {};
  if (form.units.length === 0) err.units = { message: 'At least one unit is required when not skipping.' };
  for (let i = 0; i < form.units.length; i++) {
    const u = form.units[i];
    if (u.status === 'Occupied' && u.addTenantNow && u.tenant) {
      if (!u.tenant.name?.trim()) err[`unit_${i}_name`] = { message: `Unit ${u.unitNumber || i + 1}: tenant name required` };
      if (!u.tenant.email?.trim()) err[`unit_${i}_email`] = { message: `Unit ${u.unitNumber || i + 1}: tenant email required` };
      if (!u.tenant.leaseStart) err[`unit_${i}_leaseStart`] = { message: `Unit ${u.unitNumber || i + 1}: lease start required` };
      if (!u.tenant.leaseEnd) err[`unit_${i}_leaseEnd`] = { message: `Unit ${u.unitNumber || i + 1}: lease end required` };
    }
  }
  return err;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/** When mode is 'collect', wizard does not call create API; on submit it calls onCollected with summary and closes. Use from signup "Your properties" step. */
export interface AddPropertyWizardProps {
  onClose: () => void;
  onSuccess?: (propertyId: string) => void;
  mode?: 'create' | 'collect';
  onCollected?: (data: AddPropertyCollectedSummary) => void;
}

export const AddPropertyWizard: React.FC<AddPropertyWizardProps> = ({ onClose, onSuccess, mode = 'create', onCollected }) => {
  const isSelfManagingOwner = useAppStore((s) => s.isSelfManagingOwner);
  const { data: publishedPmProfiles = [] } = useQuery({
    queryKey: ['pm-profiles', 'published'],
    queryFn: () => pmProfileApi.listPublished(),
    staleTime: 2 * 60 * 1000,
  });

  const [step, setStep] = useState(0);
  const [form, setForm] = useState<PropertyOnboardingFormState>(initialFormState);
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [completed, setCompleted] = useState<Set<string>>(new Set());
  const [submitErrors, setSubmitErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const createPropertyMutation = useCreateProperty();
  const createUnitMutation = useCreateUnit();
  const createTenantMutation = useCreateTenant();
  const createLeaseMutation = useCreateLease();

  const update = useCallback((key: keyof PropertyOnboardingFormState, value: unknown) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const visibleSteps = React.useMemo(() => {
    return MASTER_STEPS.filter(s => !s.condition || s.condition(form));
  }, [form]);

  const goTo = useCallback((next: number) => {
    setStep(next);
    setSubmitErrors({});
  }, []);

  const goNext = useCallback(() => {
    const currentStepConfig = visibleSteps[step];
    if (currentStepConfig.key === 'basics') {
      const err = validateBasics(form);
      if (Object.keys(err).length > 0) {
        setSubmitErrors(err);
        return;
      }
    }
    if (currentStepConfig.key === 'currentTenant') {
      const u = form.units[0];
      const t = u?.tenant;
      const err: FormErrors = {};
      if (!t?.name?.trim()) err.tenantName = { message: 'Tenant name is required' };
      if (!t?.email?.trim()) err.tenantEmail = { message: 'Tenant email is required' };
      if (!t?.leaseStart?.trim()) err.tenantLeaseStart = { message: 'Lease start date is required' };
      if (!t?.leaseEnd?.trim()) err.tenantLeaseEnd = { message: 'Lease end date is required' };
      if (Object.keys(err).length > 0) {
        setSubmitErrors(err);
        return;
      }
    }
    if (currentStepConfig.key === 'units') {
      const err = validateUnits(form);
      if (Object.keys(err).length > 0) {
        setSubmitErrors(err);
        return;
      }
    }
    if (currentStepConfig.key === 'compliance') {
      const yearBuiltNum = form.yearBuilt ? Number.parseInt(form.yearBuilt, 10) : Number.NaN;
      if (!isNaN(yearBuiltNum) && yearBuiltNum < 1978 && !form.leadBasedPaintDisclosure) {
        setSubmitErrors({ leadBasedPaintDisclosure: { message: 'Lead-based paint disclosure is required for properties built before 1978.' } });
        return;
      }
    }
    setCompleted((c) => new Set([...c, currentStepConfig.key]));
    setSubmitErrors({});
    goTo(Math.min(step + 1, visibleSteps.length - 1));
  }, [step, form, goTo, visibleSteps]);

  const goBack = useCallback(() => {
    goTo(Math.max(step - 1, 0));
  }, [step, goTo]);

  const skipStep = useCallback(() => {
    const currentStepConfig = visibleSteps[step];
    setSkipped((s) => new Set([...s, currentStepConfig.key]));
    goTo(Math.min(step + 1, visibleSteps.length - 1));
  }, [step, goTo, visibleSteps]);

  const handleSubmit = useCallback(async () => {
    const basicsErr = validateBasics(form);
    if (Object.keys(basicsErr).length > 0) {
      setSubmitErrors(basicsErr);
      return;
    }
    const yearBuiltNum = form.yearBuilt ? Number.parseInt(form.yearBuilt, 10) : Number.NaN;
    if (!isNaN(yearBuiltNum) && yearBuiltNum < 1978 && !form.leadBasedPaintDisclosure) {
      setSubmitErrors({ leadBasedPaintDisclosure: { message: 'Lead-based paint disclosure is required for properties built before 1978.' } });
      return;
    }

    if (mode === 'collect' && onCollected) {
      onCollected({
        name: form.name.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        zip: form.zip.trim(),
        yearBuilt: form.yearBuilt.trim(),
        type: form.type,
        status: form.status,
      });
      onClose();
      return;
    }

    setIsSubmitting(true);
    setSubmitErrors({});

    try {
      const propertyId = newClientEntityId();
      const fullAddress = [form.address, form.city, form.state, form.zip].filter(Boolean).join(', ') || form.address;
      const occupancyNum = form.occupancyRate ? Math.min(100, Math.max(0, Number(form.occupancyRate))) : 0;
      const noiNum = form.noi ? Number(form.noi) : 0;
      const marketRentNum = form.marketRent ? Number(form.marketRent) : undefined;

      const lateFeeTypeApi = form.lateFeeType === 'combined' ? 'percentage' : form.lateFeeType;
      const lateFeeAmountNum =
        form.lateFeeType === 'fixed'
          ? (form.lateFeeAmount ? Number(form.lateFeeAmount) : undefined)
          : (form.lateFeePercentage ? Number(form.lateFeePercentage) : form.lateFeeAmount ? Number(form.lateFeeAmount) : undefined);
      const lateFeeConfig = form.lateFeeEnabled
        ? {
          enabled: true,
          type: lateFeeTypeApi,
          amount: lateFeeAmountNum,
          gracePeriodDays: form.lateFeeDays ? Number.parseInt(form.lateFeeDays, 10) : undefined,
          maxLateFee: form.lateFeeMax ? Number(form.lateFeeMax) : undefined,
        }
        : undefined;

      const ownerIdsFiltered = form.ownerIds.filter((id) => id?.trim());
      const investorIdsFiltered = form.investorIds.filter((id) => id?.trim());

      const isSelfManagedFromForm = isSelfManagingOwner ? form.managementType === 'self' : undefined;
      const propertyManagerIdFromForm = isSelfManagingOwner && form.managementType === 'pm' ? (form.managerId?.trim() || undefined) : undefined;

      let managementFeePayload: Partial<Pick<Property, 'managementFeeType' | 'managementFeeValue'>> = {};
      if (isSelfManagingOwner && form.managementType === 'pm' && form.managementFeeAmount.trim() !== '') {
        const amt = Number(form.managementFeeAmount);
        if (form.managementFeeType === 'flat') {
          managementFeePayload = { managementFeeType: 'FLAT_FEE_MONTHLY', managementFeeValue: amt };
        } else if (form.managementFeeType === 'percentage') {
          managementFeePayload = { managementFeeType: 'PERCENT_OF_COLLECTED', managementFeeValue: amt };
        }
      }

      const propertyPayload: Property = {
        id: propertyId,
        name: form.name.trim(),
        address: fullAddress,
        type: form.type,
        status: form.status,
        units: form.units.length,
        occupancyRate: occupancyNum,
        noi: noiNum,
        image: form.image?.trim() || null,
        city: form.city?.trim() || null,
        state: form.state?.trim() || null,
        zip: form.zip?.trim() || null,
        yearBuilt: form.yearBuilt ? Number.parseInt(form.yearBuilt, 10) : undefined,
        description: form.description?.trim() || null,
        amenities: form.amenities.length ? form.amenities : undefined,
        marketRent: marketRentNum,
        lateFeeConfig,
        ownerIds: ownerIdsFiltered.length ? ownerIdsFiltered : undefined,
        managerId: form.managerId?.trim() || null,
        ...(isSelfManagedFromForm !== undefined && { isSelfManaged: isSelfManagedFromForm }),
        ...(propertyManagerIdFromForm !== undefined && { propertyManagerId: propertyManagerIdFromForm }),
        ...managementFeePayload,
        portfolioId: form.portfolioId?.trim() || null,
        investorIds: investorIdsFiltered.length ? investorIdsFiltered : undefined,
        repairApprovalThreshold: typeof form.operations.repairApprovalThresholdCents === 'number' ? form.operations.repairApprovalThresholdCents : undefined,
        utilityConfigs: form.operations.utilityProvider ? [{ propertyId: propertyId, type: 'Electric', paidBy: 'Tenant', providerName: form.operations.utilityProvider }] : undefined,
        propertyAccess: form.operations.accessCode ? [{ propertyId: propertyId, type: 'Lockbox', code: form.operations.accessCode }] : undefined,
        sopEnrollments: form.operations.autoSOP ? [{ propertyId: propertyId, sopTemplateId: 'soptemp-turn', autoTrigger: true }] : undefined,
        ...(form.affordableProgram && {
          affordableProgram: {propertyId, id: `ahp-${propertyId}`, programType: form.programType, compliancePeriodEnd: form.compliancePeriodEnd || new Date().toISOString().slice(0, 10), amiPercentage: Number.parseInt(form.amiPercentage.replace('%', ''), 10) || 60, setAsideUnits: Number.parseInt(form.setAsideUnits, 10) || 0, complianceStatus: form.complianceStatus},
        }),
      };

      const created = await createPropertyMutation.mutateAsync(propertyPayload);
      const createdId = created.id;

      const createdUnitIds: { tempId: string; id: string }[] = [];
      if (form.units.length > 0) {
        for (let i = 0; i < form.units.length; i++) {
          const u = form.units[i];
          const unitPayload: Omit<Unit, 'id' | 'floorPlanId'> & { floorPlanId?: string } = {
            propertyId: createdId,
            type: form.type,
            status: u.status,
            rent: typeof u.rent === 'number' ? u.rent : 0,
            sqft: typeof u.sqft === 'number' ? u.sqft : undefined,
            bedrooms: typeof u.bedrooms === 'number' ? u.bedrooms : undefined,
            bathrooms: typeof u.bathrooms === 'number' ? u.bathrooms : undefined,
            unitNumber: u.unitNumber || undefined,
          };
          try {
            const createdUnit = await createUnitMutation.mutateAsync(unitPayload);
            createdUnitIds.push({ tempId: u.tempId, id: createdUnit.id });
          } catch (unitErr) {
            showToast(`Failed to create unit ${u.unitNumber || i + 1}: ${(unitErr as Error).message}`, 'error');
          }
        }
      }

      for (let i = 0; i < form.units.length; i++) {
        const u = form.units[i];
        if (u.status !== 'Occupied' || !u.addTenantNow || !u.tenant) continue;
        const unitId = createdUnitIds.find((x) => x.tempId === u.tempId)?.id;
        if (!unitId) continue;
        const t = u.tenant;
        if (!t.name?.trim() || !t.email?.trim() || !t.leaseStart || !t.leaseEnd) continue;

        try {
          const tenantPayload = {unitId, propertyId: createdId, name: t.name.trim(), email: t.email.trim(), phone: t.phone?.trim() || '', leaseStart: t.leaseStart, leaseEnd: t.leaseEnd, status: 'Current' as const};
          const createdTenant = await createTenantMutation.mutateAsync(tenantPayload);
          const start = new Date(t.leaseStart);
          const end = new Date(t.leaseEnd);
          const months = Math.max(1, Math.round((end.getTime() - start.getTime()) / (30 * 24 * 60 * 60 * 1000)));
          const leasePayload = {unitId, tenantId: createdTenant.id, propertyId: createdId, startDate: t.leaseStart, endDate: t.leaseEnd, monthlyRent: typeof u.rent === 'number' ? u.rent : 0, deposit: 0, status: 'Active' as const, leaseTerm: months};
          await createLeaseMutation.mutateAsync(leasePayload);
        } catch (tenantErr) {
          showToast(`Failed to add tenant for unit ${u.unitNumber || i + 1}: ${(tenantErr as Error).message}`, 'error');
        }
      }

      onClose();
      const path = `${ROUTES.PROPERTY_DETAIL.replace(':propertyId', createdId)}?onboarded=1`;
      navigate(path);
      onSuccess?.(createdId);
    } catch (err) {
      setSubmitErrors({ submit: { message: (err as Error).message } });
      showToast((err as Error).message || 'Failed to create property', 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    form,
    mode,
    onCollected,
    navigate,
    onClose,
    onSuccess,
    isSelfManagingOwner,
    createPropertyMutation,
    createUnitMutation,
    createTenantMutation,
    createLeaseMutation,
    showToast,
  ]);

  const currentStepConfig = visibleSteps[step];
  const canSkip = SKIPPABLE_KEYS.has(currentStepConfig.key);
  const isLast = step === visibleSteps.length - 1;
  const isFirst = step === 0;

  const renderCurrentStep = () => {
    switch (currentStepConfig.key) {
      case 'setup': return <SetupChecklist config={form.config} update={c => update('config', c)} />;
      case 'basics': return <StepBasics form={form} update={update} errors={submitErrors} isSelfManagingOwner={isSelfManagingOwner} publishedPmProfiles={publishedPmProfiles} />;
      case 'details': return <StepDetails form={form} update={update} />;
      case 'currentTenant': return <StepCurrentTenant form={form} update={update} />;
      case 'units': return <StepUnits form={form} update={update} errors={submitErrors} />;
      case 'financials': return <StepFinancials form={form} update={update} />;
      case 'operations': return <StepOperations form={form} update={update as any} />;
      case 'ownership': return <StepOwnership form={form} update={update} isSelfManagingOwner={isSelfManagingOwner} />;
      case 'compliance': return <StepCompliance form={form} update={update} />;
      case 'review': return <StepReview form={form} visibleSteps={visibleSteps} skipped={skipped} />;
      default: return null;
    }
  };

  const progressPct = visibleSteps.length > 0 ? Math.round(((step + 1) / visibleSteps.length) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-100 dark:bg-slate-950">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-slate-900 dark:to-slate-800 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-brand-600 text-white flex items-center justify-center">
            <Building size={18} />
          </div>
          <div>
            <h1 className="text-base font-bold text-slate-900 dark:text-white">New Property</h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">Onboarding wizard</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-600 dark:text-slate-400">
            Step {step + 1} of {visibleSteps.length}
          </span>
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300 tabular-nums">
            {progressPct}% complete
          </span>
          <div className="w-28 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-600 rounded-full transition-all duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-lg text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 transition"
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </div>

      <div className="flex flex-1 min-h-0 max-w-4xl w-full mx-auto px-4 pb-4">
        <div className="flex flex-1 min-h-0 w-full rounded-2xl shadow-2xl ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden bg-white dark:bg-slate-900">
          <aside className="w-52 shrink-0 border-r border-slate-200 dark:border-slate-800 py-4 px-2 flex flex-col gap-0.5">
            {visibleSteps.map((s, i) => {
              const active = i === step;
              const done = completed.has(s.key) && i !== step;
              const skip = skipped.has(s.key);
              const Icon = s.icon;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => goTo(i)}
                  className={`flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg transition ${active ? 'bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 border border-brand-200 dark:border-brand-800' : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${active ? 'bg-brand-600 text-white' : done ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' : 'bg-slate-200 dark:bg-slate-700 text-slate-500'
                      }`}
                  >
                    {done ? <CheckCircle2 size={16} /> : skip ? <span className="text-xs">–</span> : <Icon size={16} />}
                  </div>
                  <div className="min-w-0">
                    <div className={`text-sm font-medium truncate ${active ? 'text-brand-700 dark:text-brand-300' : 'text-slate-700 dark:text-slate-300'}`}>
                      {s.title}
                    </div>
                    {skip && <div className="text-xs text-slate-400">Skipped</div>}
                  </div>
                </button>
              );
            })}
          </aside>

          <main className="flex-1 flex flex-col min-w-0 p-6 min-h-0">
            <div className="mb-4 pb-4 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <currentStepConfig.icon size={22} className="text-brand-600" />
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">{currentStepConfig.title}</h2>
                {!currentStepConfig.required && (
                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                    OPTIONAL
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{currentStepConfig.subtitle}</p>
            </div>

            <div ref={contentRef} className="flex-1 overflow-y-auto pr-2">
              {Object.keys(submitErrors).length > 0 && (
                <FormErrorSummary
                  errors={submitErrors as React.ComponentProps<typeof FormErrorSummary>['errors']}
                  formId="property-onboarding-form"
                />
              )}
              {renderCurrentStep()}
            </div>

            <div className="flex items-center justify-between pt-4 mt-4 border-t border-slate-200 dark:border-slate-800 shrink-0">
              <button
                type="button"
                onClick={goBack}
                disabled={isFirst}
                className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium text-sm hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <ChevronLeft size={16} /> Back
              </button>
              <div className="flex items-center gap-2">
                {canSkip && (
                  <button
                    type="button"
                    onClick={skipStep}
                    className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    Skip for now
                  </button>
                )}
                {isLast ? (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-brand-600 to-brand-700 text-white font-semibold text-sm hover:from-brand-700 hover:to-brand-800 disabled:opacity-50 shadow-sm flex items-center gap-2"
                  >
                    {isSubmitting && <LoadingSpinner size="sm" />}
                    {isSubmitting ? 'Creating...' : 'Add Property'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={goNext}
                    className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-brand-600 to-brand-700 text-white font-semibold text-sm hover:from-brand-700 hover:to-brand-800 shadow-sm flex items-center gap-1"
                  >
                    Continue <ChevronRight size={16} />
                  </button>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

